import { ArrowTopRightOnSquareIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { type FC, useState } from "react";
import { Link } from "react-router-dom";

import { Dialog } from "@fronterahealth/frontera-ui-components";

import { type RulePackage } from "@api/rest/types-and-hooks";
import { PackageCard } from "@pages/QARules/PackageCard";
import { type PackageOwnerContext, canEditInPlace, packageHref } from "@pages/QARules/qaRuleLabels";

interface PackageEditActionProps {
  packageId: number;
  pkg: RulePackage | undefined;
  context: PackageOwnerContext;
  payorTypes: string[];
}

/**
 * Edit control for a package shown on an org or template page. Edits in place when
 * this org/template is the package's only user (without type change, duplicate or
 * archive, which reach beyond it); otherwise links to its page, since a change there
 * affects everyone sharing it.
 */
export const PackageEditAction: FC<PackageEditActionProps> = ({ packageId, pkg, context, payorTypes }) => {
  const [open, setOpen] = useState(false);

  if (!pkg || !canEditInPlace(pkg, context)) {
    return (
      <Link
        to={packageHref(packageId)}
        className="flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary"
        title="Shared package — edit it on the QA Rules page"
      >
        Edit in QA Rules
        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
      </Link>
    );
  }

  return (
    <>
      <button
        type="button"
        aria-label="Edit package"
        title="Only used here, so it can be edited here"
        onClick={() => setOpen(true)}
        className="text-text-tertiary hover:text-text-primary"
      >
        <PencilSquareIcon className="h-4 w-4" />
      </button>
      {open && (
        <Dialog
          open={open}
          setOpen={setOpen}
          title={`Edit ${pkg.name}`}
          size="5xl"
          overflowBehavior="yAuto"
          primaryButton={{ text: "Done", onClick: () => setOpen(false) }}
          secondaryButton={undefined}
        >
          <PackageCard pkg={pkg} payorTypes={payorTypes} defaultExpanded restricted />
        </Dialog>
      )}
    </>
  );
};
